import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Mail, Calendar, Scale, CreditCard, Clock, AlertCircle } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      {/* Header */}
      <header className="border-b border-[#1e3a5f30] bg-[#0a0f1e]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-white hover:text-[#00d4aa] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00d4aa]" />
              <span className="text-white font-medium">Legal</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#111827] border border-[#1e3a5f30] rounded-2xl p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00d4aa]/10 mb-6">
              <Scale className="w-8 h-8 text-[#00d4aa]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-[#94a3b8]">Please read these terms carefully before using our services</p>
          </div>

          {/* Last Updated */}
          <div className="flex items-center gap-2 text-[#94a3b8] text-sm mb-8 justify-center">
            <Calendar className="w-4 h-4" />
            <span>Last Updated: March 2026</span>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {/* Service Description */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">1</span>
                Service Description
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                AILeadMantra is a B2B SaaS CRM software platform designed for businesses to manage leads, automate marketing workflows, and enhance customer engagement. Our platform provides tools for lead capture, AI-powered communication, analytics, and multi-channel marketing automation.
              </p>
            </section>

            {/* Subscription & Billing */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">2</span>
                Subscription & Billing
              </h2>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span>Subscription billing is processed <strong className="text-white">monthly</strong> on a recurring basis.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span>You may <strong className="text-white">cancel anytime</strong>. Your subscription will remain active until the end of the current billing period.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">No partial refunds</strong> are provided for unused portions of a billing period.</span>
                </li>
              </ul>
            </section>

            {/* Cancellation */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">3</span>
                Cancellation Policy
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                Upon cancellation, your account will remain <strong className="text-white">active until the end of the current billing period</strong>. After this period, you will lose access to premium features but may retain access to your data for a limited time as outlined in our data retention policy.
              </p>
            </section>

            {/* Refunds */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">4</span>
                Refund Policy
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                We offer a <strong className="text-white">7-day money-back guarantee</strong> for first-time subscribers only. After the initial 7 days, no refunds will be issued. Please refer to our <Link to="/refund" className="text-[#00d4aa] hover:underline">Refund Policy</Link> for complete details.
              </p>
            </section>

            {/* Governing Law */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">5</span>
                Governing Law
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                These Terms of Service are governed by and construed in accordance with the laws of <strong className="text-white">India</strong>. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-[#00d4aa]/10 to-[#00d4aa]/5 rounded-xl p-6 border border-[#00d4aa]/20">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00d4aa]" />
                Contact Us
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <a 
                href="mailto:hello@aileadmantra.com" 
                className="inline-flex items-center gap-2 mt-3 text-[#00d4aa] hover:text-[#00b894] font-medium transition-colors"
              >
                <Mail className="w-4 h-4" />
                hello@aileadmantra.com
              </a>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1e3a5f30] bg-[#0a0f1e] py-8 mt-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold">© 2026 AILeadMantra</p>
              <p className="text-[#94a3b8] text-sm">B2B SaaS Platform</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors">Home</Link>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#robots" className="text-[#94a3b8] hover:text-white transition-colors">Features</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#pricing" className="text-[#94a3b8] hover:text-white transition-colors">Pricing</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="mailto:hello@aileadmantra.com" className="text-[#94a3b8] hover:text-white transition-colors">Contact</a>
            </div>
            <div className="flex gap-4 text-sm">
              <Link to="/terms" className="text-[#00d4aa] font-medium">Terms</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/privacy" className="text-[#94a3b8] hover:text-white transition-colors">Privacy</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/refund" className="text-[#94a3b8] hover:text-white transition-colors">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
